import React, { ReactNode } from 'react';

import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip';
import Icon from '~/UI/Icon';
import { Link } from 'react-router-dom';
import buildProductSearchUrl from '~/Router/buildProductSearchUrl';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import { Query } from '~/Product';
import CountryIcon from '~/UI/CountryIcon';
import { IoSearchCircleOutline } from 'react-icons/io5';
import { Country } from '~/Api/Country';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/CzMaxPrice';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';
import { Input } from '~/libs/ui/atoms/Input';
import { FaExclamationCircle } from 'react-icons/fa';
import { CompilanceIconWithTooltip } from '~/libs/ui/molecules/CompilanceIconWithTooltip';
import { ProductCompliance } from '~/Api/CzSuklCompliance';
import GeneralProduct from '~/Api/Product/Product';
import { PriceSelect } from '~/libs/ui/atoms/PriceSelect';
import { calculateProductPackagePrice } from '~/Product';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { RiFormula } from 'react-icons/ri';

export interface ReferencePriceValue {
	value: string;
	parentValue: null;
	referencePriceItem: ReactNode;
}

type OnChangeListener = (
	product: GeneralProduct,
	referencePrice: ReferencePriceValue | null,
	referencePrices: ReferencePrice[],
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;

type OnSelectListener = (product: GeneralProduct, selected: boolean, range: boolean) => void;

export interface ProductValues {
	referencePrice: {
		value: ReferencePriceValue | null;
		isValid: boolean;
	};
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean | null;
	};
	packageSize: {
		value: string;
		isValid: boolean | null;
	};
	selected: boolean;
}

interface RowProps {
	product: GeneralProduct;
	singleUnitPackageSize: boolean;
	referencePrices: ReferencePrice[];
	productCompliance: ProductCompliance;
	highlightProduct: boolean;
	referencePackageSize: number | null;
	lastSelectedProduct: boolean;
	disableSelection?: boolean;
	lowestPriceIndex: number | null;
	checked: boolean;
	checkRow(value: boolean): void;
	values: ProductValues;
	detailLink(product: { id: string }): { pathname: string; search: string };
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
}

interface RowEmptyProps {
	countryAlpha: string;
	countryName: string;
}

export interface ProductRow {
	country: Country;
	product: Product | null;
	lowestPriceIndex: number | null;
}

interface TableProps {
	products: ProductRow[];
	productValues: { [key in string]: ProductValues };
	highlightProductId: string | null;
	referencePackageSize: number | null;
	disableSelection?: boolean;
	lastSelectedProductId: string | null;
	checkedRows: GeneralProduct[];
	checkRow(product: GeneralProduct, value: boolean): void;
	detailLink(product: { id: string }): { pathname: string; search: string };
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
}

export function Table({
	products,
	productValues,
	highlightProductId,
	referencePackageSize,
	disableSelection,
	lastSelectedProductId,
	checkedRows,
	checkRow,
	detailLink,
	onChange,
	onSelect,
	onSave,
}: TableProps) {
	return (
		<>
			<Header />
			{products.map((optionalProduct) => {
				const { country, product, lowestPriceIndex } = optionalProduct;

				return product !== null ? (
					<Row
						key={product.product.id}
						product={product.product}
						singleUnitPackageSize={product.singleUnitPackageSize}
						referencePrices={product.referencePrices}
						productCompliance={product.productCompliance}
						detailLink={detailLink}
						disableSelection={disableSelection}
						highlightProduct={highlightProductId === product.product.id}
						checked={checkedRows?.some((row) => row.id === product.product.id)}
						checkRow={(value) => checkRow(product.product, value)}
						lastSelectedProduct={lastSelectedProductId === product.product.id}
						lowestPriceIndex={lowestPriceIndex}
						referencePackageSize={referencePackageSize}
						values={productValues[product.product.id]}
						onChange={onChange}
						onSave={onSave}
						onSelect={onSelect}
					/>
				) : (
					<RowEmpty key={country.alpha2} countryAlpha={country.alpha2} countryName={country.name} />
				);
			})}
		</>
	);
}

function Header() {
	return (
		<div className='product-row-header'>
			<div className='w-9 grow-0 shrink-0' />
			<div className='pl-1 w-20 grow-0 shrink-0'>Country</div>
			<div className='pl-3 flex-1'>Brand name</div>
			<div className='pl-3 flex-1'>Company</div>
			<div className='pl-3 flex-1'>Dosage Form</div>
			<div className='pl-3 flex-1'>Dosage strength</div>
			<div className='pl-3 flex-1'>Package</div>
			<div className='pl-3 pr-5 grow-0 shrink-0 w-40 text-right'>
				Price
				<TailwindTooltip
					reference={<Icon name='violet-question-mark' />}
					displayClass='relative -top-px inline-block ml-2 h-4 align-top'
					position='left'
				>
					<p className='leading-snug'>
						<strong>Horní cena:</strong> Ex-factory price (zveřejněná, nebo vypočítaná dle SP-CAU-010)
					</p>
					<p className='leading-snug'>
						<strong>Spodní cena (zvýrazněná tučně):</strong> Původní cena zveřejněná ve zdroji (Ex-factory price, nebo
						cena sloužící pro přepočet na Ex-factory price).
					</p>
				</TailwindTooltip>
			</div>
			<div className='pl-2 grow-0 shrink-0 w-16 text-right'>Package for calc.</div>
			<div className='pl-3 grow-0 shrink-0 w-32 text-right'>
				Calc. Price
				<TailwindTooltip
					reference={<Icon name='violet-question-mark' />}
					displayClass='relative -top-px inline-block ml-2 h-4 align-top'
					position='left'
				>
					<p className='leading-snug'>
						<strong>Calc. price:</strong> Cena vzniklá vynásobením ceny (pole Price) velikostí balení (pole Package for
						calc.)
					</p>
					<p className='leading-snug'>
						<strong>Horní cena:</strong> Ex-factory price (zveřejněná, nebo vypočítaná dle SP-CAU-010)
					</p>
					<p className='leading-snug'>
						<strong>Spodní cena (zvýrazněná tučně):</strong> Původní cena zveřejněná ve zdroji (Ex-factory price, nebo
						cena sloužící pro přepočet na Ex-factory price)
					</p>
				</TailwindTooltip>
			</div>
			<div className='w-16 grow-0 shrink-0 text-right'>Use for calc.</div>
			<div className='w-9 grow-0 shrink-0' />
		</div>
	);
}

function RowEmpty({ countryAlpha, countryName }: RowEmptyProps) {
	const searchLink = buildProductSearchUrl(
		new Query([new FilterParameter('country', countryAlpha)]),
		null,
		'first-available',
		null
	);

	return (
		<Link to={searchLink} className='product-row-empty'>
			<div className='pl-1 w-20 grow-0 shrink-0 flex items-center'>
				<div className='w-auto grow shrink flex flex-nowrap justify-start items-center'>
					<CountryIcon code={countryAlpha} name={countryName} />
					<span className='product-row-empty-country'>{countryAlpha}</span>
				</div>
			</div>
			<div className='pl-3 flex-1 text-[13px] flex items-center'>
				<p className='mr-2'>No referencing product selected for the country.</p>{' '}
				<span className='product-row-empty-link'>
					<IoSearchCircleOutline className='w-5 h-5 mr-0.5' /> Search for product
				</span>
			</div>
		</Link>
	);
}

function Row({
	product,
	singleUnitPackageSize,
	referencePrices,
	productCompliance,
	highlightProduct,
	referencePackageSize,
	disableSelection,
	lastSelectedProduct,
	lowestPriceIndex,
	checked,
	checkRow,
	values,
	detailLink,
	onChange,
	onSelect,
	onSave,
}: RowProps) {
	const detailLinkString = detailLink(product);

	function renderLowerPriceIndex(index: number) {
		switch (index) {
			case 0:
				return 'Lowest';
			case 1:
				return '2nd lowest';
			case 2:
				return '3rd lowest';
			default:
				return `${index}. lowest`;
		}
	}

	function renderPackagePrice(referencePrice: ReferencePrice | null): ReactNode {
		if (referencePrice === null) {
			return null;
		}

		if (
			values.packageSize.isValid === false ||
			(values.packageSize.isValid === null && values.packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize =
			values.packageSize.value !== '' ? parseHumanFloat(values.packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}

		const { calculatedPrice, sourcePrice } = referencePrice;

		return (
			<div data-testid='cell-calc-price'>
				{calculateProductPackagePrice(
					calculatedPrice.price,
					parsedPackageSize,
					referencePackageSize
				).roundedHumanFormat()}
				<strong className='product-row-price-local'>
					{sourcePrice.price !== null
						? calculateProductPackagePrice(
								sourcePrice.price,
								parsedPackageSize,
								referencePackageSize
						  ).roundedHumanFormat()
						: null}
				</strong>
			</div>
		);
	}

	const referencePrice = values.referencePrice.value?.value
		? referencePrices.find((price) => price.calculatedPrice.type === values.referencePrice.value?.value) ?? null
		: null;

	const referencePriceItem = referencePrice && (
		<>
			<div>{referencePrice.calculatedPrice.price.roundedHumanFormat()}</div>
			<strong className='block leading-normal'>{referencePrice.sourcePrice.price?.roundedHumanFormat()}</strong>
		</>
	);

	const selectionChanged = (selected: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		onSelect(product, selected, event.nativeEvent.shiftKey);
	};

	const onSelectPrice = (value: string, reactNode: ReactNode) => {
		const optionValue = {
			value,
			parentValue: null,
			referencePriceItem: reactNode,
		};
		onChange(product, optionValue, referencePrices, values.packageSize.value, values.packageSizeForSingleUnit.value);
	};

	return (
		<div
			className={`product-row${highlightProduct ? ' product-row-highlight' : ''} ${
				product.status.isRemoved() || product.status.isBeforeIntroduction() ? 'product-row-removed' : ''
			}`}
			data-testid='row'
			key={product.id}
		>
			<div
				className='relative inline-flex h-full w-9 items-center justify-center grow-0 shrink-0'
				data-testid='delete-product'
			>
				<Checkbox id={`product-select-${product.id}`} checked={checked} onChange={checkRow} />
			</div>
			<div className='pl-1 w-20 grow-0 shrink-0'>
				<Link className='inline-flex items-center justify-center w-full h-full' tabIndex={-1} to={detailLinkString}>
					<CountrySourceWithTooltip
						countryAlpha={product.dataset.source.country.alpha2}
						countryName={product.dataset.source.country.name}
						dataSourceShortName={product.dataset.source.shortName}
						dataSourceDescription={product.dataset.source.description}
					/>
				</Link>
			</div>
			<div className='pl-3 flex-1'>
				<Link
					className='flex flex-col justify-center w-full h-full'
					tabIndex={-1}
					to={detailLinkString}
					title={product.uppercasedBrandName ? product.uppercasedBrandName : ''}
				>
					<span className='product-row-text'>{product.uppercasedBrandName}</span>
				</Link>
			</div>
			<div className='pl-3 flex-1'>
				<Link
					className='inline-flex flex-col justify-center w-full h-full'
					tabIndex={-1}
					to={detailLinkString}
					title={product.uppercasedMarketingAuthorizationHolder ? product.uppercasedMarketingAuthorizationHolder : ''}
				>
					<span className='product-row-text'>{product.uppercasedMarketingAuthorizationHolder}</span>
				</Link>
			</div>
			<div className='pl-3 flex-1'>
				<Link
					className='inline-flex flex-col justify-center w-full h-full'
					tabIndex={-1}
					to={detailLinkString}
					title={product.uppercasedDosageForm ? product.uppercasedDosageForm : ''}
				>
					<span className='product-row-text'>{product.uppercasedDosageForm}</span>
				</Link>
			</div>
			<div className='pl-3 flex-1'>
				<Link
					className='inline-flex flex-col justify-center w-full h-full'
					tabIndex={-1}
					to={detailLinkString}
					title={product.dosageStrength ? product.dosageStrength : ''}
				>
					<span className='product-row-text'>{product.dosageStrength}</span>
				</Link>
			</div>
			<div className='pl-3 flex-1'>
				{singleUnitPackageSize ? (
					<label htmlFor={`product-package-${product.id}`} className='w-full max-w-14 h-full flex items-center'>
						<Input
							id={`product-package-${product.id}`}
							name={`product-package-${product.id}`}
							value={values.packageSizeForSingleUnit.value}
							isInvalid={values.packageSizeForSingleUnit.isValid === false}
							onBlur={onSave}
							onChange={(value) =>
								onChange(product, values.referencePrice.value, referencePrices, values.packageSize.value, value)
							}
						/>
					</label>
				) : (
					<Link
						className='inline-flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
						title={product.packageSize ? product.packageSize : ''}
					>
						<span className='product-row-text'>{product.packageSize}</span>
					</Link>
				)}
			</div>
			<div className='pl-3 pr-5 w-40 relative grow-0 shrink-0'>
				{referencePrices.length > 1 ? (
					<div className='inline-flex flex-col justify-center w-full h-full'>
						{referencePrice && (
							<PriceSelect referencePrice={referencePrice} referencePrices={referencePrices} onSelect={onSelectPrice} />
						)}
					</div>
				) : (
					<div className='inline-flex flex-col justify-center w-full h-full'>
						<abbr
							className='product-row-price product-row-price-tooltip'
							data-testid='cell-reference-price'
							title={referencePrice ? referencePrice.calculatedPrice.description : ''}
						>
							{referencePrice && (
								<span className='absolute left-full top-0 -ml-5 -mt-0.5 w-3 h-3 leading-3 rounded-full text-[11px] font-semibold bg-white text-center'>
									?
								</span>
							)}
							{referencePriceItem}
						</abbr>
					</div>
				)}
				{singleUnitPackageSize && (
					<TailwindTooltip
						reference={<FaExclamationCircle className='w-4 h-4 text-red-600' />}
						displayClass='!absolute top-1/2 right-0 -mt-2 inline-block h-4 align-top'
						position='left'
					>
						Single unit package size
					</TailwindTooltip>
				)}
			</div>
			<div className='pl-2 w-16 grow-0 shrink-0'>
				<label
					htmlFor={`product-package-for-calc-${product.id}`}
					className='flex items-center w-full h-full cursor-text'
				>
					<Input
						id={`product-package-for-calc-${product.id}`}
						name={`product-package-for-calc-${product.id}`}
						value={values.packageSize.value}
						placeholder={`${referencePackageSize ?? ''}`}
						isInvalid={values.packageSize.isValid === false}
						onBlur={onSave}
						onChange={(value) =>
							onChange(
								product,
								values.referencePrice.value,
								referencePrices,
								value,
								values.packageSizeForSingleUnit.value
							)
						}
					/>
				</label>
			</div>
			<div className='pl-3 w-32 grow-0 shrink-0'>
				<Link
					data-testid='calculated-price'
					className='inline-flex flex-col justify-center w-full h-full'
					tabIndex={-1}
					to={detailLinkString}
				>
					<div className='product-row-price'>{renderPackagePrice(referencePrice)}</div>
				</Link>
			</div>
			<div className='w-16 grow-0 shrink-0 text-right'>
				<div className='relative flex justify-end items-center h-full w-full grow-0 shrink-0'>
					<CompilanceIconWithTooltip
						compliance={productCompliance.resolution}
						title={productCompliance.resolveRuleDescription()}
						decisionComment={productCompliance.decisionComment}
						referenceDataSourceVersion={productCompliance.czechReferenceDataSources.version}
						referenceDataSourceSince={productCompliance.czechReferenceDataSources.effectiveSince}
					/>
					<div className='ml-1'>
						<Checkbox
							id={`product-selection-${product.id}`}
							checked={!disableSelection && values.selected}
							disabled={disableSelection}
							focused={lastSelectedProduct}
							onChange={selectionChanged}
						/>
					</div>
				</div>
			</div>
			<div className='w-9 flex grow-0 shrink-0'>
				<Link className='inline-flex items-center justify-center w-full h-full' tabIndex={-1} to={detailLinkString}>
					{lowestPriceIndex !== null && (
						<TailwindTooltip
							reference={<RiFormula className='inline-block mt-1 w-3 h-3 align-top' />}
							displayClass='relative inline-block w-5 h-5 rounded-full text-black bg-gray-200 text-center align-midle'
							position='left'
						>
							<strong className='block mb-0.5'>Lowest price:</strong>
							{renderLowerPriceIndex(lowestPriceIndex)} ex-factory price
						</TailwindTooltip>
					)}
				</Link>
			</div>
		</div>
	);
}
